import type { StreamingBlock } from "@storytell/streaming";
import { TbAlertCircle, TbChevronDown, TbChevronUp } from "solid-icons/tb";
import { Show, createMemo, createSignal } from "solid-js";

interface ErrorRendererProps {
  block: StreamingBlock;
}

export const ErrorRenderer = (props: ErrorRendererProps) => {
  const [isExpanded, setIsExpanded] = createSignal(false);

  const errorContent = createMemo(() => {
    const content = props.block.finalContent;
    if (content?.type === "error") {
      return {
        displayMessage: content.displayMessage,
        systemDetail: content.systemDetail,
        reason: content.reason,
      };
    }
    return null;
  });

  const hasDetails = createMemo(() => {
    const content = errorContent();
    return !!content?.systemDetail;
  });

  return (
    <Show when={errorContent()}>
      {(error) => (
        <div class="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 mb-4">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <TbAlertCircle size={20} class="text-red-600 dark:text-red-500" />
            </div>

            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Unable to complete operation</h4>

              <p class="text-sm text-red-700 dark:text-red-400">{error().displayMessage}</p>

              <Show when={hasDetails()}>
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  class="mt-2 flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                >
                  <Show when={isExpanded()} fallback={<TbChevronDown size={14} />}>
                    <TbChevronUp size={14} />
                  </Show>
                  <span>{isExpanded() ? "Hide" : "Show"} technical details</span>
                </button>

                <Show when={isExpanded()}>
                  <div class="mt-2 p-2 rounded bg-red-100 dark:bg-red-900/30 text-xs text-red-800 dark:text-red-300 font-mono">
                    {error().systemDetail}
                  </div>
                </Show>
              </Show>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
};
